import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  PriorityHigh as PriorityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getLeadPriorities, createLeadPriority, updateLeadPriority, deleteLeadPriority } from '../../services/master';

const LeadPriority = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [leadPriorityData, setLeadPriorityData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedLeadPriorityForDelete, setSelectedLeadPriorityForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingLeadPriority, setEditingLeadPriority] = useState(null);
  const [newLeadPriority, setNewLeadPriority] = useState({
    leadPriority: '',
    status: 'Active',
  });

  // Fetch lead priorities
  const fetchLeadPriorities = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const leadPriorities = await getLeadPriorities();
      setLeadPriorityData(leadPriorities);
    } catch (err) {
      setApiError('Failed to load lead priorities.');
      setLeadPriorityData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadPriorities();
  }, []);

  // Filter logic
  const getFilteredLeadPriorities = () => {
    return leadPriorityData.filter((lp) => {
      const matchesSearch =
        lp.leadPriority.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || lp.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredLeadPriorities = getFilteredLeadPriorities();

  // Pagination
  const paginatedLeadPriorities = filteredLeadPriorities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newLeadPriority.leadPriority.trim()) err.leadPriority = 'Lead Priority is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return leadPriorityData.some(
      (lp) =>
        lp.leadPriority.toLowerCase() === newLeadPriority.leadPriority.trim().toLowerCase() &&
        lp.id !== editingLeadPriority?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingLeadPriority(null);
    setNewLeadPriority({
      leadPriority: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingLeadPriority(null);
    setNewLeadPriority({
      leadPriority: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (leadPriority) => {
    setEditingLeadPriority(leadPriority);
    setNewLeadPriority({
      leadPriority: leadPriority.leadPriority,
      status: leadPriority.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (leadPriority) => {
    setSelectedLeadPriorityForDelete(leadPriority);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedLeadPriorityForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await deleteLeadPriority(selectedLeadPriorityForDelete.id);
      setAlertConfig({ title: 'Success', message: 'Lead priority deleted successfully!' });
      setAlertOpen(true);
      fetchLeadPriorities(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete lead priority.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedLeadPriorityForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ leadPriority: 'Lead priority already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'leadPriority',
        details: {
          leadPriority: newLeadPriority.leadPriority.trim(),
        },
        status: newLeadPriority.status === 'Active',
      };

      if (editingLeadPriority) {
        await updateLeadPriority(editingLeadPriority.id, payload);
      } else {
        await createLeadPriority(payload);
      }

      setAlertConfig({
        title: 'Success',
        message: editingLeadPriority ? 'Lead priority updated successfully!' : 'Lead priority added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchLeadPriorities(true);
    } catch (err) {
      setErrors({ leadPriority: 'Failed to save lead priority.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchLeadPriorities(true);
  };

  const handleChange = (field, value) => {
    setNewLeadPriority((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PriorityIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Lead Priority Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadPriorities()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>
              Add Lead Priority
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the lead priority <strong>{selectedLeadPriorityForDelete?.leadPriority}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadPriorities()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Lead Priority..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredLeadPriorities.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} lead priorities matching your criteria.` : 'No lead priorities found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !leadPriorityData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Lead Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLeadPriorities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No lead priorities found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeadPriorities.map((leadPriority, index) => (
                    <TableRow key={leadPriority.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{leadPriority.leadPriority}</TableCell>
                      <TableCell>
                        <Chip
                          label={leadPriority.status}
                          color={leadPriority.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(leadPriority)} disabled={isLoading}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteClick(leadPriority)} disabled={isLoading}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredLeadPriorities.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLeadPriority ? 'Edit Lead Priority' : 'Add New Lead Priority'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lead Priority"
                  value={newLeadPriority.leadPriority}
                  onChange={(e) => handleChange('leadPriority', e.target.value)}
                  error={!!errors.leadPriority}
                  helperText={errors.leadPriority}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newLeadPriority.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingLeadPriority ? 'Update' : 'Add'} Lead Priority
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LeadPriority;
